import type { Messages } from './en';

/**
 * Fase E (paquet taroko): Gorja de Taroko i platja de Qixingtan.
 * Les línies de pràctica d'anglès es queden en anglès expressament.
 * A les frases dels personatges es manté la marca “English practice:” tal qual:
 * el joc la fa servir per saber quina frase ha de llegir en veu alta en anglès.
 */
export const caTaroko: Pick<Messages, 'taroko'> = {
  taroko: {
    mapNames: { hualien: 'Gorja de Taroko i Qixingtan' },
    zones: {
      shrine: 'Santuari de la Primavera Eterna',
      buluowan: 'Terrassa de Buluowan',
      shakadang: 'Sender de Shakadang',
      qingshui: 'Penya-segats de Qingshui',
      village: 'Poble de Qixingtan',
      visitor: 'Centre de visitants de Taroko',
    },
    objects: {
      tk_visitor: 'Centre de visitants',
      tk_shuttleTaroko: 'Parada del Tourist Shuttle (Taroko)',
      tk_shuttleBeach: 'Parada del Tourist Shuttle (Qixingtan)',
      tk_cimu: 'Pavelló del pont Cimu',
      tk_rockSign: 'Senyal de despreniments',
      tk_swallows: 'Cartell de la Gruta de les Orenetes',
      tk_shrine: 'Santuari de la Primavera Eterna',
      tk_bell: 'Campanar',
      tk_buluowan: 'Cartell de Buluowan',
      tk_weave: 'Teler truku',
      tk_monkeys: 'Avís de micos',
      tk_mochi: 'Parada de mochi de mill',
      tk_shakadang: 'Cartell del sender de Shakadang',
      tk_bikeRent: 'Lloguer de bicis',
      tk_skip: 'Pedres planes',
      tk_sunrise: "Pont de l'Alba",
      tk_stargaze: "Mirador d'estrelles",
      tk_museum: 'Museu del bonítol',
      tk_sausage: 'Parada de botifarres de peix volador',
    },
    npc: {
      tk_guide: 'Guia turística',
      tk_rangerB: 'Guarda del sender',
      tk_weaver: 'Teixidora truku',
      tk_mochiAuntie: 'Tieta truku',
      tk_fisher: 'Pescador',
      tk_kid1: 'Nen que fa saltar pedres',
      tk_kid2: 'Nena que fa saltar pedres',
      tk_cyclist: 'Ciclista',
      tk_photographer: 'Fotògrafa',
      tk_bonito: 'Guia del museu',
      tk_stargazer: "Guia d'estrelles",
    },
    items: {},

    common: {
      firstStar: '⭐ Nou descobriment!',
      notHere: 'Mmm, no passa res.',
    },
    visitor: {
      text: 'Centre de visitants del Parc Nacional de Taroko. Una gran maqueta en 3D mostra la gorja: 19 km de penya-segats de marbre excavats pel riu Liwu.',
      ask: "🗺️ Preguntar el camí (en anglès)",
      q: 'Com es pregunta el camí amb educació?',
      // English practice
      a: ['Excuse me, how do I get to the Shakadang Trail?', 'Trail where? Tell me.', 'Shakadang now, go go go!'],
      right: '“Walk west along the river and cross the red bridge. The stairs go down to the trail!”',
      wrong: 'El voluntari somriu: «Prova-ho d’una manera més educada: comença amb “Excuse me…”».',
    },
    cimu: {
      text: 'Pont Cimu (慈母橋): «el pont de la mare amorosa». Una mare esperava aquí cada dia el seu fill, que construïa la carretera de muntanya. El pont va rebre aquest nom pel seu amor.',
      listen: '🌉 Mirar el pont vermell',
      looked: 'Dos lleons de marbre blanc vigilen el pont. A sota, el riu baixa blanc i ràpid.',
      // English practice
      phrase: 'Thank you, Mom! I love you.',
    },
    rock: {
      text: 'Un triangle groc amb pedres que cauen. Poden caure roques dels penya-segats en qualsevol moment.',
      q: 'Què vol dir aquest senyal?',
      // English practice
      a: ['Watch out for falling rocks!', 'Free rocks — take one!', 'A rock concert tonight.'],
      right: 'Exacte! No et treguis el casc i no t’aturis sota els penya-segats.',
      wrong: 'Torna-hi a mirar: cauen pedres pel pendent…',
    },
    swallows: {
      text: 'Gruta de les Orenetes (燕子口): el riu va excavar centenars de foradets al marbre, i les orenetes hi fan els nius a dins!',
      count: '🐦 Comptar les orenetes',
      q: (birds: string) => `${birds}\n\nQuantes orenetes veus?`,
      right: 'Ben comptat! Les orenetes tornen aquí cada primavera.',
      wrong: 'Torna a comptar, a poc a poc: un, dos, tres…',
      // English practice: number words.
      numbers: ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'],
    },
    shrine: {
      text: "El Santuari de la Primavera Eterna (長春祠) recorda els 226 treballadors que van morir construint la carretera que travessa l'illa pel centre. La font que hi ha a sota no para mai de rajar: per això és «eterna».",
      bow: '🙏 Fer una reverència en silenci',
      bowed: 'Et quedes quiet una estona. La cascada brama sota la teulada vermella.',
      // English practice
      phrase: '“Eternal” means “forever”.',
    },
    bell: {
      text: "Un campanar dalt d'unes escales molt empinades. Des d'aquí es veu tota la gorja!",
      ring: '🔔 Tocar la campana',
      rang: 'Dong… dong… El so rebota de penya-segat en penya-segat.',
      // English practice
      phrase: 'I rang the bell. (ring → rang → rung)',
    },
    buluowan: {
      text: 'Buluowan (布洛灣) és una terrassa on vivien i conreaven famílies truku. En llengua truku, Buluowan vol dir «eco».',
      shout: '📣 Cridar “Hello!” cap a la gorja',
      // English practice
      hello: 'Hello!',
      echo: '“Hello… hello… hello…” Les muntanyes et responen!',
    },
    weave: {
      text: 'Un teler de cintura truku. El dibuix de rombes vermells es diu «els ulls dels avantpassats»: vol dir que la família està protegida i fora de perill.',
      try: '🧵 Aprendre la paraula (en anglès)',
      q: 'Què està fent?',
      // English practice
      a: ['She is weaving cloth.', 'She is eating cloth.', 'She is sleeping on cloth.'],
      right: 'Sí! “Weave” vol dir teixir: teixeix fils vermells i blancs per fer roba.',
      wrong: 'Fixa’t com mou els fils amb les mans…',
    },
    monkeys: {
      text: 'En aquests penya-segats hi viuen macacos de Formosa (台灣獼猴). Són salvatges… i els interessen moltíssim els teus berenars!',
      q: 'Un mico mira el teu mochi. Què fas?',
      // English practice
      a: ['Don’t feed the monkeys. I keep my food in my bag.', 'I give it all my snacks!', 'I try to touch it.'],
      right: 'Correcte! El menjar de les persones fa emmalaltir els micos salvatges i els torna lladregots.',
      wrong: 'Mmm… els micos poden mossegar, i el nostre menjar els fa mal. Torna-ho a provar!',
    },
    // English practice: food order.
    mochi: { vendor: 'Millet mochi, made with our Truku millet! Peanut or plain?', ok: 'Two peanut ones, please. How much are they?', bad: ['Mochi. Give.', 'I want everything, no money.'], done: 'Tou i elàstic, amb un gust de mill que recorda la fruita seca. La tieta et saluda: «Que gaudeixis del sender!».', price: 30 },
    shakadang: {
      text: "Sender de Shakadang (砂卡礑步道): un camí excavat al penya-segat sobre un rierol d'aigua cristal·lina. “Skadang” és la paraula truku per dir queixal!",
      look: "💧 Mirar l'aigua",
      q: "De quin color és l'aigua?",
      // English practice
      a: ['It’s blue-green and very clear.', 'It’s brown and muddy.', 'It’s bright red.'],
      right: 'Sí! Es veuen tots els còdols de marbre blanc del fons.',
      wrong: "Fixa't bé en el rierol…",
    },
    bike: {
      text: 'Lloguer de bicis: el carril bici recorre tota la costa de Qixingtan.',
      ride: '🚲 Llogar una bici (en anglès)',
      q: 'Com ho demanes?',
      // English practice
      a: ['I’d like to rent a bike for one hour, please.', 'Bike. One. Now.', 'Give me your bike.'],
      right: 'Pedales arran de costa amb el vent als cabells. Muntanyes a l’esquerra i el Pacífic a la dreta!',
      wrong: 'L’amo riu: «M’ho pots dir amb més educació?».',
      price: 20,
      poor: 'No tens prou monedes.',
    },
    skip: {
      text: 'Còdols plans i llisos: perfectes per fer-los saltar sobre les onades.',
      throw: '🥏 Llançar una pedra',
      again: '🥏 Un altre cop',
      result: (n: number, words: string) => (n === 0 ? 'Xof! S’ha enfonsat de seguida.' : `Salta, salta… ha saltat ${n} ${n === 1 ? 'vegada' : 'vegades'}! ${words}`),
      great: 'Increïble! Els nens t’aplaudeixen.',
    },
    sunrise: {
      text: "El Pont de l'Alba (曙光橋). Hualien és un dels primers llocs de Taiwan on es veu sortir el sol sobre el Pacífic.",
      early: "🌅 Fer una foto de la sortida del sol",
      photo: "📸 Clic! El sol surt de l'oceà, taronja i daurat. Quina fotografia!",
      late: 'El sol ja és alt. Torna abans de les 7 del matí per fer la foto de la sortida del sol!',
      // English practice
      phrase: 'The sun rises in the east.',
    },
    stars: {
      text: "Un mirador d'estrelles. Qixingtan (七星潭) vol dir «Llac de les Set Estrelles», per les set estrelles del Carro (l'Óssa Major).",
      look: '✨ Mirar el cel',
      day: 'És de dia i les estrelles estan amagades. Torna quan es faci fosc!',
      q: 'Quantes estrelles té el Carro?',
      // English practice
      a: ['Seven.', 'Three.', 'Twenty.'],
      right: "Set! Segueix les dues estrelles de l'extrem del «cassó» i trobaràs l'Estrella Polar.",
      wrong: 'Torna a comptar les estrelles en forma de cassó…',
    },
    museum: {
      text: 'Museu del Bonítol de Qixingtan (柴魚博物館). Qixingtan era un poble de pescadors: fumaven i assecaven el bonítol fins que quedava dur com la fusta.',
      learn: '🐟 Aprendre una curiositat',
      q: 'Per què en xinès al bonítol sec li diuen «peix llenya» (柴魚)?',
      // English practice
      a: ['Because dried bonito is as hard as wood.', 'Because it lives in trees.', 'Because it is cooked on a campfire only.'],
      right: 'Correcte! Després es ratlla en làmines fines que «ballen» sobre el tofu calent.',
      wrong: 'Pista: dona uns copets a un bonítol sec… toc, toc!',
    },
    // English practice: food order.
    sausage: { vendor: 'Flying-fish sausage! A Hualien speciality. With garlic?', ok: 'Yes, please. One sausage with garlic.', bad: ['Sausage! Fast!', 'Fish. Garlic. Mine.'], done: "Fumada, sucosa i una mica salada, com el mar. Perfecta després d'un bany!", price: 35 },

    talk: {
      ranger: {
        intro: 'Benvingut a Taroko! Abans dels senders de la gorja, una pregunta ràpida de seguretat.',
        q: 'Què has de portar posat als senders de la gorja?',
        // English practice
        a: ['A helmet.', 'Flip-flops.', 'Sunglasses only.'],
        right: 'Molt bé! Demana’n un a la caseta de cascos: és gratis.',
        wrong: 'Pensa en les pedres que cauen…',
      },
      tk_guide: [
        'Please follow me and stay together! This way, everyone! (English practice)',
        'En llengua truku, Taroko vol dir «magnífic i preciós».',
        "El nostre autobús surt a les tres. No arribeu tard!",
      ],
      tk_rangerB: [
        'Si us plau, no us atureu sota els penya-segats. Continueu caminant.',
        '“Watch out!” vol dir «compte, que ve alguna cosa!».',
        'Fa milions d’anys, aquest marbre era el fons del mar.',
      ],
      tk_weaver: [
        "La meva àvia em va ensenyar a teixir. El vermell és la vida i el coratge.",
        'English practice: “What are you making?” — “I’m making a bag.”',
        'Antigament, les noies truku aprenien a teixir abans de casar-se.',
      ],
      tk_mochiAuntie: [
        'El mill creix bé a la muntanya. En fem vi i mochi.',
        'English practice: “How much is it?” — “Thirty dollars.”',
      ],
      tk_fisher: [
        'Surto abans de la sortida del sol. Al matí els peixos tenen gana!',
        'English practice: “Did you catch anything?” — “Yes, three fish!”',
        "Aquí el mar es fa fondo molt de pressa. No et banyis: només mulla't els peus.",
      ],
      tk_kid1: [
        'El meu rècord són set salts! El pots superar?',
        'Busca una pedra plana i llança-la baixa i ràpida. Així!',
      ],
      tk_kid2: [
        'English practice: “Your turn!” — “Okay, here I go!”',
        'També apilem pedres. La meva és una torre de deu!',
      ],
      tk_cyclist: [
        '“On your left!”: això és el que es diu en anglès quan avances algú.',
        'El carril bici arriba fins a la ciutat. Vint quilòmetres!',
      ],
      tk_photographer: [
        'Els penya-segats de Qingshui fan mil metres. No em caben a la càmera!',
        'English practice: “Could you take a photo of us, please?”',
        "Vine a la sortida del sol: el mar es torna rosa i daurat.",
      ],
      tk_bonito: [
        'English practice: “Would you like to try some?” — “Yes, please!”',
        "Les escates de bonítol es fumen durant molts dies. En notes l'olor?",
      ],
      tk_stargazer: [
        'Mira amunt! Aquella tan brillant no és una estrella: és un planeta.',
        'English practice: “Can you see the Big Dipper?” — “Yes, I can!”',
        'Lluny dels llums de la ciutat, aquí es veu la Via Làctia.',
      ],
    },
  },
};
